import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, EnvelopeSimple } from '@phosphor-icons/react';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background-default">
      <SEO 
        title="Terms of Service"
        description="Luster Adorn Terms of Service - Read the terms governing your use of our website and services."
        url="/terms-of-service"
      />
      
      {/* Hero */}
      <section className="bg-background-subtle py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FileText size={48} className="text-primary mx-auto mb-4" />
            <h1 className="font-display text-4xl text-secondary mb-4">Terms of Service</h1>
            <p className="font-body text-text-secondary">
              Last updated: February 3, 2026
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="prose prose-lg max-w-none"
          >
            <p className="font-body text-text-secondary leading-relaxed mb-8">
              These Terms of Service ("Terms") govern your access to and use of lusteradorn.com and our services. By using our site, you agree to these Terms. If you do not agree, please do not use our services.
            </p>

            <div className="space-y-10">
              {/* Use of Our Services */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Use of Our Services</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  You must be 18 or older to use our site. You agree not to misuse our services, including unauthorized access, interfering with functionality, or violating laws.
                </p>
              </div>

              {/* Products and Orders */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Products and Orders</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We sell imitation jewelry as a retailer. Product descriptions, images, and prices are for reference; slight variations may occur. All orders are subject to availability and confirmation. We reserve the right to cancel orders due to pricing errors or stock issues.
                </p>
              </div>

              {/* Pricing and Payment */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Pricing and Payment</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  Prices are in INR and exclude taxes/shipping unless stated. We accept major credit/debit cards, UPI, and net banking via secure gateways. Full payment is required upfront.
                </p>
              </div>

              {/* Shipping and Delivery */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Shipping and Delivery</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  Shipping times vary by location within India (typically 3-7 business days). We are not responsible for delays by carriers. Risk of loss passes to you upon delivery.
                </p>
              </div>

              {/* Returns and Refunds */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Returns and Refunds</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                To be eligible for any return or refund, you must record a clear, continuous unboxing video from the moment you receive and open the sealed package, showing the parcel condition, product, and any damage.
                <br/><br/>
                Returns are only accepted for products that are received in a damaged or defective condition, and only when supported by valid unboxing video proof as described above. Items must be unused and in their original packaging.
                <br/><br/>
                Eligible returns for damaged or defective products (with valid unboxing video proof) may be processed as store credit or exchange, at our discretion. Shipping charges are not refundable. Custom or personalized items are non-returnable.
                <br/><br/>
                Once your return is approved, refunds or store credit are typically processed within 5–10 business days.

                </p>
              </div>

              {/* Intellectual Property */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Intellectual Property</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  All content on our site, including images and designs, is our property or licensed to us. You may not reproduce or distribute without permission.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Limitation of Liability</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  Our liability is limited to the purchase price. We disclaim warranties for indirect damages. Products are sold "as is."
                </p>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Governing Law</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  These Terms are governed by Indian law, with disputes resolved in courts of Maharashtra.
                </p>
              </div>

              {/* Changes to Terms */}
              <div>
                <h2 className="font-display text-2xl text-secondary mb-4">Changes to Terms</h2>
                <p className="font-body text-text-secondary leading-relaxed">
                  We may update these Terms. Continued use constitutes acceptance.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="mt-12 p-6 bg-background-subtle rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <EnvelopeSimple size={24} className="text-primary" />
                <h3 className="font-display text-lg text-secondary">Questions?</h3>
              </div>
              <p className="font-body text-text-secondary">
                Contact us at{' '}
                <a href="mailto:info@lusteradorn.com" className="text-primary hover:underline">
                  info@lusteradorn.com
                </a>
              </p>
            </div>

            {/* Related Links */}
            <div className="mt-8 pt-8 border-t border-border-default">
              <p className="font-body text-sm text-text-muted">
                See also:{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
